/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { toast } from "../../components/ui/sonner";
import { Users, UserPlus, UserMinus, Hourglass, Network, ShieldAlert, Workflow, CalendarClock, Clock, LogIn, LogOut, Loader2, Sparkles, RefreshCw, Newspaper, Megaphone, CalendarDays, ChevronDown, ChevronUp, Bell, Plus, ExternalLink, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { fmtDT, fmtD } from "../../lib/date";
import { inp } from "./hrmsShared";
import { OverdueTransitionsBoard } from "./OverdueTransitionsBoard";

// 「今日」以浏览器本地日期为准（YYYY-MM-DD），与 /hrms/attendance/clock 的写入口径保持一致。
// 修改原因（2026-10-14 · HR 反馈「HR Dashboard 无法打卡 / 打了卡看不到」）：
//   后端 /hrms/overview 原先固定用 UTC 日期查 todayClock，而打卡写入用的是员工本地日期，
//   美西下午（UTC 已跨日）会出现「打了卡但看板显示未打卡」；同时顶栏曾用 MM/DD/YYYY，
//   与看板查询口径不一致。这里显式传本地日期，后端按同一日期读取。
// 最终效果：打卡后看板即时显示，且跨时区不再错位。
const localDate = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; };

const Card = ({ icon: Icon, label, value, tone, onClick, tid }) => (
  <button data-testid={tid} onClick={onClick} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-left kpi-accent card-soft pl-5 pr-4 py-4 hover:shadow-lift transition-shadow ${tone || ""}`}>
    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold tracking-wide"><Icon className="h-4 w-4 text-brand-500" />{label}</div>
    <p className="text-2xl sm:text-3xl font-extrabold font-display tracking-tight text-slate-900 mt-1.5">{value}</p>
  </button>
);

export default function HRMSDashboard() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const nav = useNavigate();
  const [ov, setOv] = useState(null);
  const [denied, setDenied] = useState(false);   // 403 → 明确的「无权查看」，不再渲染一堆 0
  const [busy, setBusy] = useState("");
  const [insight, setInsight] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [digest, setDigest] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  // Add-ons：公告 Newsletter（二-19）与 HR 日历（二-20）
  const [news, setNews] = useState(null);
  const [cal, setCal] = useState(null);
  const [newsDlg, setNewsDlg] = useState(null);
  const [calDlg, setCalDlg] = useState(null);
  const [showBasis, setShowBasis] = useState(false);
  const CAL_KINDS = { evaluation: "绩效评估", training: "培训", birthday: "员工生日", reminder: "内部提醒", meeting: "会议", holiday: "假期/节假日" };
  const month = () => new Date().toISOString().slice(0, 7);
  const loadNews = useCallback(() => { api.get("/hrms/newsletter").then(({ data }) => setNews(data)).catch(() => setNews({ rows: [] })); }, [api]);
  const loadCal = useCallback(() => { api.get("/hrms/calendar", { params: { month: month() } }).then(({ data }) => setCal(data)).catch(() => setCal({ rows: [], kinds: {} })); }, [api]);
  useEffect(() => { loadNews(); loadCal(); }, [loadNews, loadCal]);
  const saveNews = async () => {
    if (!newsDlg.title?.trim()) return toast.error(T("请填写公告标题", "Title required"));
    try { const { data } = await api.post("/hrms/newsletter", newsDlg); toast.success(T(`公告已发布并推送给 ${data.notified} 人`, `Published to ${data.notified} people`)); setNewsDlg(null); loadNews(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("发布失败", "Publish failed")); }
  };
  const delNews = async (n) => {
    if (!window.confirm(T(`删除公告「${n.title}」？`, `Delete announcement "${n.title}"?`))) return;
    try { await api.delete(`/hrms/newsletter/${n.id}`); loadNews(); } catch { toast.error(T("删除失败", "Delete failed")); }
  };
  const saveCal = async () => {
    if (!calDlg.title?.trim() || !calDlg.date) return toast.error(T("请填写标题与日期", "Title and date required"));
    try { await api.post("/hrms/calendar", calDlg); toast.success(T("日历事件已添加", "Calendar event added")); setCalDlg(null); loadCal(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
  };
  const delCal = async (ev) => {
    try { await api.delete(`/hrms/calendar/${ev.id}`); loadCal(); } catch { toast.error(T("删除失败", "Delete failed")); }
  };
  const runDigest = async () => {
    setDigestBusy(true);
    try {
      const period = new Date().toISOString().slice(0, 7);
      const { data } = await api.post("/hrms/digest/run", { period, force: true });
      setDigest(data);
      toast.success(T(`${data.period} HR 月度简报已生成并推送 ${data.notified} 位管理层`, `${data.period} HR monthly digest sent to ${data.notified} manager(s)`));
    } catch (e) { toast.error(e?.response?.data?.detail || T("简报生成失败", "Digest failed")); }
    setDigestBusy(false);
  };
  // 看板数据加载（2026-10-26 补充拒绝态）：后端 /hrms/overview 已按权限收口，
  //   非 HR 管理岗会拿到 403。原实现 `.catch(() => setOv({}))` 把 403 也当成「空数据」，
  //   于是没权限的账号（如招聘专员）看到的是「在职人数 0 / 尚未关联员工档案」——
  //   看起来像系统坏了或数据丢了，而不是「你没有这个权限」。
  //   现在单独记 denied，页面直接给明确的「无权查看」提示，不再渲染一堆 0。
  const reload = useCallback(() => {
    api.get("/hrms/overview", { params: { localDate: localDate() } })
      .then(({ data }) => { setOv(data); setDenied(false); })
      .catch((e) => { setOv({}); setDenied(e?.response?.status === 403); });
  }, [api]);
  useEffect(() => { reload(); }, [reload]);

  const clock = async (action) => {
    setBusy(action);
    try {
      const now = new Date();
      const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const localTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      await api.post("/hrms/attendance/clock", { action, localDate, localTime });
      toast.success(action === "in" ? T(`上班打卡成功 ${localTime}`, `Clocked in ${localTime}`) : T(`下班打卡成功 ${localTime}`, `Clocked out ${localTime}`));
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("打卡失败", "Clock failed")); }
    setBusy("");
  };
  const loadInsight = async (refresh = 0) => {
    setAiBusy(true);
    try { const { data } = await api.get("/hrms/ai-insight", { params: { refresh } }); setInsight(data); }
    catch (e) { toast.error(e?.response?.data?.detail || T("AI 洞察生成失败", "AI insight failed")); }
    setAiBusy(false);
  };

  if (!ov) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  const tc = ov.todayClock;
  const canView = ov.canManage || ov.deptScope;
  return (
    <div className="space-y-5" data-testid="hrms-dashboard">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{T("人力资源看板", "HR Dashboard")}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{T("组织编制 · 员工档案 · 项目人力 · 入离职编排 · 考勤假勤（总体设计方案 V1.0 · Phase 1）", "Org & headcount · Employee records · Project staffing · On/offboarding · Attendance & leave")}</p>
        </div>
        {canView && (
          <button data-testid="hrms-digest-btn" disabled={digestBusy} onClick={runDigest}
            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-xs rounded-full bg-slate-800 hover:bg-slate-700 text-white font-semibold px-3 py-1.5 disabled:opacity-50">
            {digestBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Newspaper className="h-3.5 w-3.5" />}{T("生成 HR 月度简报", "Generate HR Monthly Digest")}
          </button>
        )}
      </div>
      {denied && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="hrms-dashboard-denied">
          <p className="text-sm font-semibold text-slate-700">{T("无权查看人力资源看板", "No access to the HR dashboard")}</p>
          <p className="mt-1 text-xs text-slate-400">{T("该模块已被管理员限制访问，如需权限请联系管理员。", "This module is restricted. Contact your administrator for access.")}</p>
        </div>
      )}
      {!denied && digest && (
        <div className="rounded-xl border border-slate-300 bg-slate-50 p-3 text-xs text-slate-600 leading-relaxed" data-testid="hrms-digest-msg">{digest.message}</div>
      )}

      {!denied && (<>
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 flex flex-wrap items-center gap-4" data-testid="hrms-clock-card">
        <div className="flex items-center gap-2 text-indigo-700 font-bold text-sm"><Clock className="h-4 w-4" />{T("今日打卡", "Today's Clock")}</div>
        {ov.myEmployee ? (
          <>
            <span className="text-xs text-slate-500" data-testid="hrms-clock-status">
              {tc?.clockIn ? `${T("上班", "In")} ${tc.clockIn}` : T("未打上班卡", "Not clocked in")} · {tc?.clockOut ? `${T("下班", "Out")} ${tc.clockOut}` : T("未打下班卡", "Not clocked out")}
              {tc?.hours ? ` · ${tc.hours}h${tc.ot15 ? ` (OT1.5× ${tc.ot15}h)` : ""}${tc.ot20 ? ` (OT2× ${tc.ot20}h)` : ""}` : ""}
            </span>
            <div className="flex-wrap md:flex-nowrap flex gap-2 ml-auto">
              <button data-testid="hrms-clock-in-btn" disabled={!!busy || !!tc?.clockIn} onClick={() => clock("in")}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-2 disabled:opacity-40">
                {busy === "in" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogIn className="h-3.5 w-3.5" />}{T("上班打卡", "Clock In")}
              </button>
              <button data-testid="hrms-clock-out-btn" disabled={!!busy || !tc?.clockIn || !!tc?.clockOut} onClick={() => clock("out")}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2 disabled:opacity-40">
                {busy === "out" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}{T("下班打卡", "Clock Out")}
              </button>
            </div>
          </>
        ) : (
          <span className="text-xs text-slate-500" data-testid="hrms-clock-nolink">{T("您的账号尚未关联员工档案，HR 同步或关联后即可打卡", "Your account is not linked to an employee record yet — clocking is available after HR links it")}</span>
        )}
      </div>

      {/* Add-ons：公告 Newsletter + HR 日历（对应 HR 反馈「Front Page Newsletter」「HR Calendar」） */}
      <div className="grid lg:grid-cols-2 gap-3">
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="hrms-news-card">
          <div className="flex items-center gap-2 mb-2">
            <Megaphone className="h-4 w-4 text-amber-600" />
            <p className="text-sm font-bold text-amber-800">{T("公告栏 Newsletter", "Newsletter")}</p>
            {ov.canManage && <button data-testid="hrms-news-add-btn" onClick={() => setNewsDlg({ title: "", content: "", pinned: false, targetDept: "" })}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-[11px] rounded-full border border-amber-300 text-amber-700 hover:bg-amber-100 font-semibold px-3 py-1"><Plus className="h-3 w-3" />{T("发布公告", "Post")}</button>}
          </div>
          {(news?.rows || []).length === 0 ? <p className="text-xs text-slate-400">{T("暂无公告。HR / 行政可在这里发布全员公告。", "No announcements yet. HR / Office Management post here.")}</p> : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {news.rows.map((n) => (
                <div key={n.id} className="rounded-lg bg-white border border-amber-100 px-3 py-2" data-testid={`hrms-news-${n.id}`}>
                  <div className="flex items-center gap-2">
                    {n.pinned && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 font-bold">{T("置顶", "PIN")}</span>}
                    <p className="text-xs font-bold text-slate-700 flex-1 truncate">{n.title}</p>
                    <span className="text-[10px] text-slate-400">{fmtDT(n.createdAt)}</span>
                    {ov.canManage && <button data-testid={`hrms-news-del-${n.id}`} onClick={() => delNews(n)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-300 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>}
                  </div>
                  {n.content && <p className="text-[11px] text-slate-500 mt-0.5 whitespace-pre-wrap">{n.content}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-4" data-testid="hrms-cal-card">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="h-4 w-4 text-sky-600" />
            <p className="text-sm font-bold text-sky-800">{T(`HR 日历 · ${cal?.month || month()}`, `HR Calendar · ${cal?.month || month()}`)}</p>
            <span className="text-[11px] text-slate-400">{cal?.gcalConfigured ? T("Google 已配置", "Google connected") : T("Google 未配置（可用 ICS 订阅）", "Google not configured (ICS available)")}</span>
            {ov.canManage && <button data-testid="hrms-cal-add-btn" onClick={() => setCalDlg({ kind: "reminder", title: "", date: new Date().toISOString().slice(0, 10), time: "", note: "", location: "" })}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-[11px] rounded-full border border-sky-300 text-sky-700 hover:bg-sky-100 font-semibold px-3 py-1"><Plus className="h-3 w-3" />{T("新增事件", "Add event")}</button>}
          </div>
          {(cal?.rows || []).length === 0 ? <p className="text-xs text-slate-400">{T("本月暂无事件。可添加评估 / 培训 / 员工生日 / 内部提醒的截止日。", "No events this month. Add evaluation / training / birthday / reminder deadlines.")}</p> : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {cal.rows.map((ev) => (
                <div key={ev.id} className="rounded-lg bg-white border border-sky-100 px-3 py-2 flex items-center gap-2" data-testid={`hrms-cal-${ev.id}`}>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-semibold">{(cal.kinds || CAL_KINDS)[ev.kind] || ev.kind}</span>
                  <span className="text-[11px] text-slate-500">{fmtD(ev.date)}{ev.time ? ` ${ev.time}` : ""}</span>
                  <p className="text-xs font-semibold text-slate-700 flex-1 truncate">{ev.title}</p>
                  <a href={ev.googleUrl} target="_blank" rel="noreferrer" title={T("加入 Google 日历", "Add to Google Calendar")} className="text-slate-400 hover:text-sky-600"><ExternalLink className="h-3.5 w-3.5" /></a>
                  {ov.canManage && <button data-testid={`hrms-cal-del-${ev.id}`} onClick={() => delCal(ev)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-300 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>}
                </div>
              ))}
            </div>
          )}
          <a href={`${(api.defaults?.baseURL || "/api")}/hrms/calendar.ics`} onClick={() => toast.success(T("已下载 ICS：可在 Google/Outlook 日历中「订阅」实现联动", "ICS downloaded — subscribe in Google/Outlook to sync"))}
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-sky-700 font-semibold hover:underline" data-testid="hrms-cal-ics">
            <CalendarDays className="h-3 w-3" />{T("下载 ICS（订阅到 Google / Outlook 日历）", "Download ICS (subscribe in Google / Outlook)")}
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card tid="hrms-stat-active" icon={Users} label={T("在职人数", "Active Staff")} value={ov.active ?? 0} onClick={() => nav("/hrms/employees")} />
        <Card tid="hrms-stat-probation" icon={Hourglass} label={T("试用期", "Probation")} value={ov.probation ?? 0} onClick={() => nav("/hrms/employees?status=probation")} />
        <Card tid="hrms-stat-onboard" icon={UserPlus} label={T("本月入职", "Hired This Month")} value={ov.monthOnboard ?? 0} onClick={() => nav("/hrms/employees")} />
        <Card tid="hrms-stat-left" icon={UserMinus} label={T("本月离职", "Departed This Month")} value={ov.monthLeft ?? 0} onClick={() => nav("/hrms/transitions")} />
        <Card tid="hrms-stat-headcount" icon={Network} label={T("批准编制", "Approved Headcount")} value={ov.approvedHeadcount ?? 0} onClick={() => nav("/hrms/org")} />
      </div>
      {canView && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card tid="hrms-stat-over" icon={Network} label={T("超编岗位", "Overstaffed Roles")} value={ov.overStaffed ?? 0} tone={ov.overStaffed ? "border-red-300" : ""} onClick={() => nav("/hrms/org")} />
          <Card tid="hrms-stat-under" icon={Network} label={T("缺编岗位", "Understaffed Roles")} value={ov.underStaffed ?? 0} tone={ov.underStaffed ? "border-amber-300" : ""} onClick={() => nav("/hrms/org")} />
          <Card tid="hrms-stat-cred" icon={ShieldAlert} label={T("证书临期/过期", "Credentials Expiring")} value={ov.credAlerts ?? 0} tone={ov.credAlerts ? "border-amber-300" : ""} onClick={() => nav("/hrms/employees")} />
          <Card tid="hrms-stat-tr" icon={Workflow} label={T("进行中流程", "Open Workflows")} value={ov.openTransitions ?? 0} tone={ov.openTransitions ? "border-indigo-300" : ""} onClick={() => nav("/hrms/transitions")} />
          <Card tid="hrms-stat-leave" icon={CalendarClock} label={T("待审批假勤", "Pending Leave Approvals")} value={ov.pendingLeaves ?? 0} tone={ov.pendingLeaves ? "border-amber-300" : ""} onClick={() => nav("/hrms/leaves")} />
        </div>
      )}

      {canView && <OverdueTransitionsBoard api={api} zh={zh} />}

      {canView && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4" data-testid="hrms-ai-card">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-violet-600" />
            <p className="text-sm font-bold text-violet-800">{T("AI 人力洞察", "AI Workforce Insight")}</p>
            {insight?.at && <span className="text-[11px] text-slate-400">{insight.cached ? T("缓存 · ", "cached · ") : ""}{fmtDT(insight.at)}</span>}
            <button data-testid="hrms-ai-refresh-btn" disabled={aiBusy} onClick={() => loadInsight(1)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-[11px] rounded-full border border-violet-300 text-violet-600 hover:bg-violet-100 font-semibold px-3 py-1 disabled:opacity-50">
              <RefreshCw className={`h-3 w-3 ${aiBusy ? "animate-spin" : ""}`} />{T("重新分析", "Re-analyze")}
            </button>
          </div>
          {insight ? (
            <>
              <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed" data-testid="hrms-ai-text">{insight.text}</pre>
              {/* 依据透明化（对应 HR 反馈「AI 风险依据不透明」）：展开可查看喂给 AI 的结构化依据与来源 */}
              <button data-testid="hrms-ai-basis-toggle" onClick={() => setShowBasis((v) => !v)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:underline">
                {showBasis ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}{T("查看判断依据 / 数据来源", "Show basis / data sources")}
              </button>
              {showBasis && (
                <div className="mt-2 rounded-lg border border-violet-200 bg-white p-3 text-[11px] text-slate-600 space-y-1.5" data-testid="hrms-ai-basis">
                  <p className="font-bold text-slate-700">{T("AI 判断依据（原始数据）", "AI basis (raw inputs)")}</p>
                  {Object.entries(insight.basisMeta || {}).map(([k, m]) => (
                    <p key={k} className="flex flex-wrap gap-1"><span className="font-semibold text-slate-700">{k}</span><span className="text-slate-400">{T("来源", "Source")}：{m.source}（{T("条数", "count")} {m.count}）</span></p>
                  ))}
                  <pre className="whitespace-pre-wrap font-sans bg-slate-50 rounded p-2 mt-1 max-h-52 overflow-y-auto" data-testid="hrms-ai-basis-raw">{JSON.stringify(insight.basis || {}, null, 2)}</pre>
                </div>
              )}
            </>
          ) : (
            <button data-testid="hrms-ai-load-btn" disabled={aiBusy} onClick={() => loadInsight(0)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2 disabled:opacity-50">
              {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("生成洞察（编制缺口 / 项目负载 / 风险建议）", "Generate insight (headcount gaps / project load / risk advice)")}
            </button>
          )}
        </div>
      )}

      {canView && Object.keys(ov.byBranch || {}).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-branch-dist">
          <p className="text-sm font-bold text-slate-700 mb-3">{T("在职人员分布（按法人/分公司）", "Active Staff by Entity / Branch")}</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(ov.byBranch).map(([b, n]) => (
              <div key={b} className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-2 text-center">
                <p className="text-lg font-extrabold text-slate-800">{n}</p>
                <p className="text-[11px] text-slate-500">{(zh ? (ov.branchLabels || {}) : (ov.branchLabelsEn || ov.branchLabels || {}))[b] || b}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 发布公告 */}
      <Dialog open={!!newsDlg} onOpenChange={(o) => !o && setNewsDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{T("发布公告", "Post Announcement")}</DialogTitle></DialogHeader>
          {newsDlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-news-dlg">
              <label className="block text-xs text-slate-500">{T("标题 *", "Title *")}<input data-testid="hrms-news-title" className={inp} value={newsDlg.title} onChange={(e) => setNewsDlg((d) => ({ ...d, title: e.target.value }))} /></label>
              <label className="block text-xs text-slate-500">{T("正文", "Content")}<textarea data-testid="hrms-news-content" rows={4} className={inp} value={newsDlg.content} onChange={(e) => setNewsDlg((d) => ({ ...d, content: e.target.value }))} /></label>
              <label className="flex items-center gap-2 text-xs text-slate-600"><input data-testid="hrms-news-pinned" type="checkbox" checked={newsDlg.pinned} onChange={(e) => setNewsDlg((d) => ({ ...d, pinned: e.target.checked }))} />{T("置顶显示", "Pin to top")}</label>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setNewsDlg(null)} className="text-xs rounded-full border border-slate-300 text-slate-500 font-semibold px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-news-save" onClick={saveNews} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold px-4 py-2">{T("发布", "Publish")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 新增 HR 日历事件 */}
      <Dialog open={!!calDlg} onOpenChange={(o) => !o && setCalDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{T("新增 HR 日历事件", "Add HR Calendar Event")}</DialogTitle></DialogHeader>
          {calDlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-cal-dlg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">{T("类型", "Kind")}<select data-testid="hrms-cal-kind" className={inp} value={calDlg.kind} onChange={(e) => setCalDlg((d) => ({ ...d, kind: e.target.value }))}>{Object.entries(CAL_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
                <label className="text-xs text-slate-500">{T("日期 *", "Date *")}<input data-testid="hrms-cal-date" type="date" className={inp} value={calDlg.date} onChange={(e) => setCalDlg((d) => ({ ...d, date: e.target.value }))} /></label>
              </div>
              <label className="block text-xs text-slate-500">{T("标题 *", "Title *")}<input data-testid="hrms-cal-title" className={inp} value={calDlg.title} onChange={(e) => setCalDlg((d) => ({ ...d, title: e.target.value }))} placeholder={T("如 Q3 绩效评估截止 / 全员安全培训", "e.g. Q3 evaluation deadline / all-hands safety training")} /></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">{T("时间（可选）", "Time (optional)")}<input data-testid="hrms-cal-time" type="time" className={inp} value={calDlg.time} onChange={(e) => setCalDlg((d) => ({ ...d, time: e.target.value }))} /></label>
                <label className="text-xs text-slate-500">{T("地点（可选）", "Location (optional)")}<input className={inp} value={calDlg.location} onChange={(e) => setCalDlg((d) => ({ ...d, location: e.target.value }))} /></label>
              </div>
              <label className="block text-xs text-slate-500">{T("备注", "Note")}<textarea data-testid="hrms-cal-note" rows={3} className={inp} value={calDlg.note} onChange={(e) => setCalDlg((d) => ({ ...d, note: e.target.value }))} placeholder={T("可含 Google Meet / Zoom 链接", "May include a Google Meet / Zoom link")} /></label>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setCalDlg(null)} className="text-xs rounded-full border border-slate-300 text-slate-500 font-semibold px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-cal-save" onClick={saveCal} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2">{T("保存", "Save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
}
